<template>
  <div class="flow-single">
    <v-toolbar flat>
      <v-btn to="/flows" icon exact>
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <div>
        <v-text-field v-model="flow.name" dense class="mt-6 mr-2" outlined placeholder="Nome do fluxo" />
      </div>

      <v-spacer />

      <transition name="unsaved-fade">
        <v-chip v-if="hasChanges" outlined small label color="warning" class="mr-4 font-weight-medium">
          <v-icon left small>mdi-circle-medium</v-icon>
          Alterações não salvas
        </v-chip>
      </transition>

      <global-variables-dialog>
        <template #activator="{ on, attrs }">
          <ib-btn outlined class="mr-2" v-bind="attrs" v-on="on">
            <v-icon left>mdi-code-json</v-icon>
            Variáveis Globais
          </ib-btn>
        </template>
      </global-variables-dialog>

      <ib-btn outlined class="mr-2" @click="exportFlow">
        <v-icon left>mdi-file-download-outline</v-icon>
        Exportar
      </ib-btn>

      <ib-btn outlined class="mr-2" @click="openSandbox">
        <v-icon left>mdi-message-processing-outline</v-icon>
        Testar
      </ib-btn>

      <ib-btn color="error" outlined class="mr-2" @click="confirmResetDialog = true">
        <v-icon left>mdi-restore</v-icon>
        Redefinir
      </ib-btn>

      <ib-btn color="primary" :loading="saving" @click="save">
        <v-icon left>mdi-content-save-outline</v-icon>
        Salvar
      </ib-btn>
    </v-toolbar>

    <v-dialog v-model="confirmResetDialog" max-width="380">
      <v-card>
        <v-card-title class="text-h6">Redefinir fluxo?</v-card-title>
        <v-card-text>Todas as alterações não salvas serão perdidas. O fluxo voltará para o último estado
          salvo.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <ib-btn text @click="confirmResetDialog = false">Cancelar</ib-btn>
          <ib-btn color="error" @click="confirmReset">Redefinir</ib-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmLeaveDialog" max-width="380" persistent>
      <v-card>
        <v-card-title class="text-h6">Sair sem salvar?</v-card-title>
        <v-card-text>Você tem alterações não salvas. Se sair agora, todas as alterações serão perdidas.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <ib-btn text @click="cancelLeave">Cancelar</ib-btn>
          <ib-btn color="error" @click="confirmLeave">Sair sem salvar</ib-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-divider />
    <div class="flow-canvas">
      <v-progress-linear v-if="loading" indeterminate color="primary" />

      <flow-editor v-else ref="editor" v-model="flow" @ready="onEditorReady" />
    </div>

    <chatbot-sandbox v-model="sandboxDialog" type="flow_chatbot" :target-id="flowId" :title="flow.name" />
  </div>
</template>

<script>
export default {
  name: 'FlowSingle',
  components: {
    FlowEditor: () => import('@/flow-editor/FlowEditor.vue'),
    GlobalVariablesDialog: () => import('@/views/GlobalVariablesDialog.vue'),
    ChatbotSandbox: () => import('@/components/ChatbotSandbox.vue')
  },
  data() {
    return {
      loading: false,
      saving: false,
      confirmResetDialog: false,
      confirmLeaveDialog: false,
      sandboxDialog: false,
      pendingLeaveNext: null,
      flow: {
        name: 'Loading...',
        nodes: [],
      },
      savedSnapshot: null,
      hasChanges: false,
    }
  },
  watch: {
    flow: {
      deep: true,
      handler() {
        if (this.savedSnapshot === null) return
        this.hasChanges = JSON.stringify(this.flow) !== this.savedSnapshot
      }
    }
  },
  computed: {
    flowId() {
      return this.$route.params.flowId
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      this.pendingLeaveNext = next
      this.confirmLeaveDialog = true
    } else {
      next()
    }
  },
  created() {
    this.load()
  },
  mounted() {
    this.loadTheme()
  },
  methods: {
    loadTheme() {
      const stored = localStorage.getItem('theme_dark');
      const isDark = stored === null ? this.$vuetify.theme.dark : stored === 'true';
      this.$vuetify.theme.dark = isDark;
    },
    confirmReset() {
      this.confirmResetDialog = false
      this.load()
    },
    confirmLeave() {
      this.confirmLeaveDialog = false
      this.hasChanges = false
      this.pendingLeaveNext()
      this.pendingLeaveNext = null
    },
    cancelLeave() {
      this.confirmLeaveDialog = false
      this.pendingLeaveNext(false)
      this.pendingLeaveNext = null
    },
    async openSandbox() {
      if (this.hasChanges) {
        const proceed = await this.$store.dispatch('confirmDialog', {
          title: 'Alterações não salvas',
          message: 'O teste usa a última versão salva do fluxo. Deseja testar mesmo assim?'
        })

        if (!proceed) return
      }

      this.sandboxDialog = true
    },
    onEditorReady() {
      this.savedSnapshot = JSON.stringify(this.flow)
      this.hasChanges = false
    },
    async load() {
      this.loading = true
      this.hasChanges = false
      this.savedSnapshot = null

      const response = await this.$api.getFlowById(this.flowId)

      if (response.error) {
        this.$router.replace('/flows')
        return
      }

      this.flow = response.message

      setTimeout(() => {
        this.loading = false
      }, 400);
    },
    async save() {
      if (document.activeElement) {
        document.activeElement.blur();
      }

      if (!this.$refs.editor.validate()) {
        this.$toast("error", "Existem erros no fluxo")
        return
      }

      this.saving = true

      setTimeout(async () => {
        const response = await this.$api.createOrUpdateFlow(this.flow)

        if (response.error) {
          this.saving = false
          return
        }

        this.$toast("success", "Fluxo salvo com sucesso")
        this.saving = false
        this.savedSnapshot = JSON.stringify(this.flow)
        this.hasChanges = false
      }, 1000);
    },
    exportFlow() {
      try {
        const flowToExport = { ...this.flow };
        delete flowToExport._id;
        delete flowToExport.created;

        const jsonString = JSON.stringify(flowToExport, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.download = `${flowToExport.name || 'fluxo'}.json`;
        link.click();

        URL.revokeObjectURL(url);
      } catch (error) {
        this.$toast("error", "Erro ao exportar fluxo")
      }
    }
  }
}
</script>

<style>
.unsaved-fade-enter-active,
.unsaved-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.unsaved-fade-enter,
.unsaved-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.flow-single .flow-canvas {
  width: 100dvw;
  height: calc(100dvh - 65px);
  background-color: #e6f5ea;
}

.theme--dark .flow-single .flow-canvas {
  background-color: var(--gray25-base);
}
</style>